// Microsoft Clarity custom tags.
//
// Clarity has no notion of an experiment: it stores an arbitrary label on the
// session which you then use as a heatmap filter. So the entire integration is
// `clarity("set", key, value)` reaching a Clarity that is actually running.
//
// Clarity arrives via the GTM container, which means most of the `set` calls that
// matter happen before clarity.js exists. This module therefore installs the same
// queue stub Clarity's own loader installs, at import time, so those calls land in
// `window.clarity.q` and are replayed by `queue.process()` when the tag fires.
// Clarity's snippet is `c[a]=c[a]||function(){...}` — the `||` is what preserves
// our stub rather than clobbering it.
//
// Everything here is best-effort: it must never throw (app/root.tsx has no
// ErrorBoundary) and must never influence what renders. Clarity being absent —
// blocked, or simply not configured, as in localdev — is the normal case.

// Vite statically replaces this, so the branch is eliminated in production.
const DEBUG = import.meta.env.VITE_ANALYTICS_DEBUG === "true";

/** Clarity truncates beyond this; drop rather than send something it will mangle. */
const MAX_TAG_LENGTH = 255;
/** Clarity's own per-page ceiling. */
const MAX_TAGS_PER_PAGE = 128;
/**
 * If Clarity never loads, nothing ever drains the queue. Cap it so a long session
 * on an ad-blocked browser cannot grow it without bound.
 */
const MAX_QUEUED_CALLS = 300;

type ClarityQueue = IArguments[];

interface ClarityFn {
  (...args: unknown[]): void;
  q?: ClarityQueue;
  /** Clarity's real loader sets this. We must NOT — see installQueueStub. */
  v?: string;
}

declare global {
  interface Window {
    clarity?: ClarityFn;
  }
}

/**
 * Matches `queue.setup()`'s shape: calls are pushed onto `window.clarity.q` as
 * argument lists, and `.v` is never set — `queue.process()` aborts with
 * "Error CL001: Multiple Clarity tags detected" if it finds one.
 */
function installQueueStub(): void {
  if (typeof window === "undefined") return;
  try {
    if (window.clarity) return; // real Clarity, or this stub, already present

    const stub = function (this: unknown) {
      const queue = stub.q ?? (stub.q = []);
      if (queue.length >= MAX_QUEUED_CALLS) return;
      // eslint-disable-next-line prefer-rest-params -- Clarity replays `q` entries
      // with Function.prototype.apply; `arguments` is the shape its loader expects.
      queue.push(arguments);
    } as ClarityFn;

    window.clarity = stub;
  } catch {
    // Never throw.
  }
}

installQueueStub();

// One tag value per key per history entry. `clarity("set", k, v)` *accumulates*
// within a page (its `log()` pushes onto a validValues array), so setting the same
// key twice puts one session under two filter values and silently cross-
// contaminates every heatmap that filters on it.
let currentLocationKey: string | null = null;
let keysSetForLocation = new Set<string>();

/**
 * Starts a fresh tag scope. Call on every history entry — Clarity resets custom
 * tags on SPA navigation (its history proxy calls `stop()` -> `variable.reset()`
 * and restarts after ~250ms), so tags must be re-applied per location.
 */
export function beginTagScope(locationKey: string): void {
  if (currentLocationKey === locationKey) return;
  currentLocationKey = locationKey;
  keysSetForLocation = new Set();
}

/** Returns whether the tag was sent. Never throws. */
export function setTag(key: string, value: string): boolean {
  if (typeof window === "undefined") return false;
  try {
    if (!key || !value) return false;
    if (key.length > MAX_TAG_LENGTH || value.length > MAX_TAG_LENGTH) return false;
    if (keysSetForLocation.has(key)) return false;
    if (keysSetForLocation.size >= MAX_TAGS_PER_PAGE) return false;

    keysSetForLocation.add(key);
    window.clarity?.("set", key, value);
    if (DEBUG) console.info("[analytics] clarity set", key, value);
    return true;
  } catch {
    // Never throw.
    return false;
  }
}

/** Test-only reset of the per-location dedupe state. */
export function __resetTagScopeForTests(): void {
  currentLocationKey = null;
  keysSetForLocation = new Set();
}
